import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormLabel,
  ClickAwayListener,
  Collapse,
} from '@mui/material';
import { Close } from '@mui/icons-material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ChevronDown } from 'lucide-react';

function ProjectSelector({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select projects...',
  label = 'Projects',
}) {
  const [open, setOpen] = useState(false);

  const handleToggleOption = (option) => {
    const isSelected = selected.some((item) => item.id === option.id);

    if (isSelected) {
      const newSelected = selected.filter((item) => item.id !== option.id);
      onChange(newSelected);
    } else {
      onChange([...selected, option]);
    }
  };

  const handleRemoveChip = (event, optionToRemove) => {
    event.stopPropagation();
    const newSelected = selected.filter((item) => item.id !== optionToRemove.id);
    onChange(newSelected);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleInputClick = () => {
    setOpen(!open);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <FormLabel
          sx={{
            color: '#0e1e40',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
            display: 'block',
          }}
        >
          {label}
        </FormLabel>

        {/* Input Field with chips inside */}
        <Box
          onClick={handleInputClick}
          sx={{
            minHeight: '40px',
            border: '1px solid #e0e1e6',
            borderRadius: '8px',
            padding: '5px 36px 5px 12px',
            cursor: 'pointer',
            backgroundColor: 'white',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px',
            position: 'relative',
            '&:hover': {
              borderColor: '#e0e1e6',
            },
            '&:focus-within': {
              borderColor: '#3d5a8a',
              outline: '2px solid rgba(61, 90, 138, 0.2)',
            },
          }}
        >
          {/* Selected items as chips - Updated styling */}
          {selected.map((option) => (
            <Chip
              key={option.id}
              label={option.name}
              size="small"
              deleteIcon={<Close sx={{ fontSize: '18px !important' }} />}
              onDelete={(event) => handleRemoveChip(event, option)}
              sx={{
                height: '24px',
                backgroundColor: '#e8eef7',
                color: '#213d69',
                fontSize: '12px',
                fontWeight: '500',
                border: 'none',
                borderRadius: '4px',
                '& .MuiChip-label': {
                  padding: '0 8px',
                },
                '& .MuiChip-deleteIcon': {
                  color: '#213d69',
                  fontSize: '16px',
                  margin: '0 4px 0 0',
                  '&:hover': {
                    color: '#213d69',
                  },
                },
              }}
            />
          ))}

          {/* Placeholder text when no items selected */}
          {selected.length === 0 && (
            <Box sx={{ color: '#546077', fontSize: '14px', flexGrow: 1 }}>
              {placeholder}
            </Box>
          )}

          {/* Dropdown Arrow */}
          <ChevronDown
            size={16}
            color="#546077"
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
              transition: 'transform 0.2s ease',
              pointerEvents: 'none',
            }}
          />
        </Box>

        {/* Dropdown List */}
        <Collapse in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              backgroundColor: 'white',
              border: '1px solid #e0e1e6',
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(14, 30, 64, 0.08)',
              maxHeight: '240px',
              overflow: 'auto',
              mt: 0.5,
            }}
          >
            <List sx={{ padding: 0 }}>
              {options.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No projects available"
                    sx={{ color: '#7D8698' }}
                  />
                </ListItem>
              ) : (
                options.map((option) => {
                  const isSelected = selected.some((item) => item.id === option.id);

                  return (
                    <ListItem
                      key={option.id}
                      button
                      onClick={() => handleToggleOption(option)}
                      sx={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#e8eef7' : 'transparent',
                        color: isSelected ? '#213d69' : '#0e1e40',
                        '&:hover': {
                          backgroundColor: isSelected ? '#e8eef7' : '#fbfbfc',
                        },
                      }}
                    >
                      <ListItemText
                        primary={option.name}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '14px',
                            fontWeight: '500',
                          },
                        }}
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  );
}

ProjectSelector.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  selected: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
};

ProjectSelector.defaultProps = {
  options: [],
  selected: [],
  placeholder: 'Select projects...',
  label: 'Projects',
};

export default ProjectSelector;
